import { Router } from 'vue-router';
import { icons as iconsJson } from '@iconify-json/fa6-solid';
import { encodeSvgForCss, iconToHTML, getIconData } from '@iconify/utils';
import mapboxgl from 'mapbox-gl';
import useIsStoryblok from '@/composables/useIsStoryblok';
import { GeoJson, LocationGeoJsonProperties } from '@/composables/useMapGeojson'

import markerImageBlippi from '@/assets/map/markers/blippi/marker-blippi.png';
import markerImageMeekah from '@/assets/map/markers/blippi/marker-meekah.png';
import markerImageJuca from '@/assets/map/markers/blippi/marker-juca.png';
import markerImageBlippiMeekah from '@/assets/map/markers/blippi/marker-blippi-meekah.png';

import filterImageBlippi from '@/assets/map/filters/blippi/filter-blippi.png';
import filterImageMeekah from '@/assets/map/filters/blippi/filter-meekah.png';
import filterImageJuca from '@/assets/map/filters/blippi/filter-juca.png';
import filterImageBlippiMeekah from '@/assets/map/filters/blippi/filter-blippi-meekah.png';

export const imagesCharactersFallback = 'blippi';
export const markerImagesCharacters = {
  blippi: markerImageBlippi,
  meekah: markerImageMeekah,
  juca: markerImageJuca,
  'blippi-meekah': markerImageBlippiMeekah,
};
export const filterImagesCharacters = {
  blippi: filterImageBlippi,
  meekah: filterImageMeekah,
  juca: filterImageJuca,
  'blippi-meekah': filterImageBlippiMeekah,
};

export type MarkerColor = {
  bg: string;
  text: string;
};

export default function useMapLayers() {
  const { isStoryblokVisualEditor } = useIsStoryblok();

  const setupGeoJsonSource = (map: mapboxgl.Map | any, geoJson: GeoJson) => {
    // const filteredGeoJsonData = filterGeoJsonSource(map, geoJson, activeFilters.value);
    map.addSource('locations', {
      type: 'geojson',
      data: geoJson.data,
      cluster: true,
      clusterMaxZoom: 14, // Max zoom to cluster points on
      clusterRadius: 50, // Radius of each cluster when clustering points (defaults to 50)
    });
  }

  const updateGeoJsonSource = (map: mapboxgl.Map | any, geoJson: GeoJson) => {
    const source = map.getSource('locations') as mapboxgl.GeoJSONSource | undefined;
    if (!source) return;
    source.setData(geoJson.data);
  }

  const setupMarkerIconImages = (map: mapboxgl.Map | any, icons: (string | null)[]) => {
    // setup image icons
    Object.entries(markerImagesCharacters).forEach(([name, image]) => {
      const img = new Image(160, 160);
      img.onload = () => {
        map.addImage(name, img);
      };
      img.src = image;
    });

    // setup svg icons
    icons.forEach((iconName) => {
      if (!iconName) return;
      const iconData = getIconData(iconsJson, iconName);
      const iconHtml = iconToHTML(iconData?.body || '', {
        width: iconData?.width?.toString() || '24',
        height: iconData?.height?.toString() || '24',
      });
      const uri = `data:image/svg+xml;utf8,${encodeSvgForCss(iconHtml)}`;
      const img = new Image(160, 160);
      img.onload = () => {
        map.addImage(iconName, img, { sdf: true });
      };
      img.src = uri;
    });
  }

  const setupMarkerClusters = (map: mapboxgl.Map | any, markerColor: MarkerColor) => {
    const clusterConfig = [
      {
        // default
        input: ['get', 'point_count'],
        radius: 15,
        color: markerColor.text,
      },
      {
        minPointCount: 35,
        radius: 25,
        color: markerColor.text,
      },
      {
        minPointCount: 55,
        radius: 35,
        color: markerColor.text,
      },
    ];

    map.addLayer({
      id: 'cluster',
      type: 'circle',
      source: 'locations',
      filter: ['has', 'point_count'],
      paint: {
        // Use step expressions (https://docs.mapbox.com/style-spec/reference/expressions/#step)
        'circle-color': [
          'step',
          ...clusterConfig
            .map((c) => [c.input || c.minPointCount, c.color])
            .flat(),
        ],
        'circle-radius': [
          'step',
          ...clusterConfig
            .map((c) => [c.input || c.minPointCount, c.radius])
            .flat(),
        ],
        'circle-stroke-width': 6,
        'circle-stroke-color': markerColor.bg,
        'circle-stroke-opacity': 0.5,
      },
    });

    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'locations',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        // 'text-font': ['Stabil Grotesk Bold'], // TODO: Upload font in Mapbox Studio - then it can be used here
        // 'text-size': 12,
      },
      paint: {
        'text-color': markerColor.bg,
      },
    });

    // events
    map.on('mouseenter', 'cluster', () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    
    map.on('mouseleave', 'cluster', () => {
      map.getCanvas().style.cursor = '';
    });

    // zoom in on cluster when clicked
    map.on('click', 'cluster', (e: any) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['cluster'],
      });
      const clusterId = features?.[0]?.properties?.cluster_id;

      const locSource = map.getSource(
        'locations',
      ) as mapboxgl.GeoJSONSource;
      locSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        if (!map || !features?.length) return;

        const coordinates = (features[0].geometry as GeoJSON.Point).coordinates;
        map.easeTo({
          center: { lng: coordinates[0], lat: coordinates[1] },
          zoom: zoom ?? undefined,
        });
      });
    });
  }

  const setupMarkers = (map: mapboxgl.Map | any, markerColor: MarkerColor, router: Router) => {
    map.addLayer({
      id: 'unclustered-point',
      type: 'symbol',
      source: 'locations',
      filter: ['all', ['!', ['has', 'point_count']]],
      layout: {
        'icon-image': ['coalesce', ['get', 'icon'], ['image', imagesCharactersFallback]],
        'icon-size': 0.5,
        'icon-anchor': 'bottom',
        'icon-offset': [0, 20],
        'icon-allow-overlap': true,
      },
    });

    // events
    map.on('mouseenter', 'unclustered-point', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'unclustered-point', () => {
      map.getCanvas().style.cursor = '';
    });

    map.on('click', 'unclustered-point', (e: any) => {
      if (isStoryblokVisualEditor.value) return;
      if (!e.features?.length) return;

      const feature = e.features[0];
      const coordinates = (feature.geometry as GeoJSON.Point).coordinates;
      const properties = feature.properties as LocationGeoJsonProperties;

      // center marker on map
      let zoom = map.getZoom();
      if (zoom < 6) {
        zoom = 6;
      }

      map.easeTo({
        center: { lng: coordinates[0], lat: coordinates[1] },
        zoom,
      });

      let link = properties.full_slug;
      if (import.meta.env.MODE.includes('editor')) {
        link = link?.startsWith('/') ? link : '/' + link;
      } else {
        link = link?.replace(import.meta.env.VITE_STORYBLOK_BRAND_FOLDER, '');
        link = link?.startsWith('/') ? link : '/' + link;
      }
      router.push(link);
    });
  }

  return {
    setupGeoJsonSource,
    updateGeoJsonSource,
    setupMarkerIconImages,
    setupMarkerClusters,
    setupMarkers,
  }
}
